import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Building2, Store, MapPin, Star, Filter, ArrowUpRight, ArrowDownRight, CheckCircle2, XCircle, Package, Coffee, Truck, Car, Smartphone, QrCode, Wifi } from 'lucide-react';
import { KPICard } from '../components/ui/KPICard';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ScatterChart, Scatter, ZAxis } from 'recharts';
// ALDI Nord specific services
const services = [{
  id: 'aldi_liefert',
  name: 'ALDI liefert',
  icon: Truck
}, {
  id: 'parkplaetze',
  name: 'Kundenparkplatz',
  icon: Car
}, {
  id: 'paketshop',
  name: 'Paketshop',
  icon: Package
}, {
  id: 'backstation',
  name: 'Backstation',
  icon: Coffee
}, {
  id: 'aldi_app',
  name: 'ALDI App',
  icon: Smartphone
}, {
  id: 'scan_go',
  name: 'Scan & Go',
  icon: QrCode
}, {
  id: 'kunden_wlan',
  name: 'Kunden-WLAN',
  icon: Wifi
}];
const marketsData = [{
  id: 'hamburg-hafencity',
  name: 'ALDI Nord Hamburg-Hafencity',
  region: 'Nord',
  rating: 4.3,
  size: 1200,
  services: ['aldi_liefert', 'parkplaetze', 'paketshop', 'backstation', 'aldi_app', 'scan_go', 'kunden_wlan']
}, {
  id: 'berlin-kreuzberg',
  name: 'ALDI Nord Berlin-Kreuzberg',
  region: 'Ost',
  rating: 3.8,
  size: 1000,
  services: ['backstation', 'aldi_app', 'kunden_wlan']
}, {
  id: 'hannover-mitte',
  name: 'ALDI Nord Hannover-Mitte',
  region: 'Nord',
  rating: 4.6,
  size: 1100,
  services: ['aldi_liefert', 'parkplaetze', 'backstation', 'aldi_app', 'scan_go', 'kunden_wlan']
}, {
  id: 'koeln-ehrenfeld',
  name: 'ALDI Nord Köln-Ehrenfeld',
  region: 'West',
  rating: 3.5,
  size: 900,
  services: ['parkplaetze', 'backstation', 'aldi_app']
}, {
  id: 'frankfurt-sachsenhausen',
  name: 'ALDI Nord Frankfurt-Sachsenhausen',
  region: 'Mitte',
  rating: 4.4,
  size: 1050,
  services: ['aldi_liefert', 'parkplaetze', 'paketshop', 'backstation', 'aldi_app', 'scan_go']
}, {
  id: 'dortmund-innenstadt',
  name: 'ALDI Nord Dortmund-Innenstadt',
  region: 'West',
  rating: 3.9,
  size: 950,
  services: ['backstation', 'aldi_app', 'paketshop']
}, {
  id: 'leipzig-sued',
  name: 'ALDI Nord Leipzig-Süd',
  region: 'Ost',
  rating: 4.5,
  size: 1150,
  services: ['aldi_liefert', 'parkplaetze', 'backstation', 'aldi_app', 'scan_go', 'kunden_wlan']
}, {
  id: 'muenster-zentrum',
  name: 'ALDI Nord Münster-Zentrum',
  region: 'West',
  rating: 4.2,
  size: 1000,
  services: ['parkplaetze', 'backstation', 'aldi_app', 'kunden_wlan']
}, {
  id: 'osnabrueck-zentrum',
  name: 'ALDI Nord Osnabrück-Zentrum',
  region: 'Nord',
  rating: 4.1,
  size: 980,
  services: ['parkplaetze', 'backstation', 'aldi_app', 'paketshop']
}, {
  id: 'kiel-zentrum',
  name: 'ALDI Nord Kiel-Zentrum',
  region: 'Nord',
  rating: 4.4,
  size: 1050,
  services: ['aldi_liefert', 'parkplaetze', 'backstation', 'aldi_app', 'scan_go']
}];
const calculateServiceImpact = () => {
  return services.map(service => {
    const marketsWithService = marketsData.filter(m => m.services.includes(service.id));
    const marketsWithoutService = marketsData.filter(m => !m.services.includes(service.id));
    const avgWithService = marketsWithService.length > 0 ? marketsWithService.reduce((acc, m) => acc + m.rating, 0) / marketsWithService.length : 0;
    const avgWithoutService = marketsWithoutService.length > 0 ? marketsWithoutService.reduce((acc, m) => acc + m.rating, 0) / marketsWithoutService.length : 0;
    return {
      ...service,
      avgRatingWith: avgWithService,
      avgRatingWithout: avgWithoutService,
      impact: avgWithService - avgWithoutService,
      marketsCount: marketsWithService.length,
      penetration: marketsWithService.length / marketsData.length * 100
    };
  }).sort((a, b) => b.impact - a.impact);
};
const serviceImpactData = calculateServiceImpact();
const levels = [{
  id: 'services',
  name: 'Service-Analyse',
  icon: Package
}, {
  id: 'markt',
  name: 'Markt-Übersicht',
  icon: Store
}];
export function ServicesReputationPage() {
  const [selectedLevel, setSelectedLevel] = useState<'services' | 'markt'>('services');
  const avgRating = marketsData.reduce((acc, m) => acc + m.rating, 0) / marketsData.length;
  const avgServices = marketsData.reduce((acc, m) => acc + m.services.length, 0) / marketsData.length;
  const marketsByServices = [...marketsData].sort((a, b) => b.services.length - a.services.length);
  const topServiceMarkets = marketsByServices.slice(0, 3);
  const lowServiceMarkets = marketsByServices.slice(-3).reverse();
  const correlationData = marketsData.map(m => ({
    name: m.name,
    serviceCount: m.services.length,
    rating: m.rating,
    z: m.size
  }));
  const n = correlationData.length;
  const sumX = correlationData.reduce((acc, d) => acc + d.serviceCount, 0);
  const sumY = correlationData.reduce((acc, d) => acc + d.rating, 0);
  const sumXY = correlationData.reduce((acc, d) => acc + d.serviceCount * d.rating, 0);
  const sumX2 = correlationData.reduce((acc, d) => acc + d.serviceCount * d.serviceCount, 0);
  const sumY2 = correlationData.reduce((acc, d) => acc + d.rating * d.rating, 0);
  const correlation = (n * sumXY - sumX * sumY) / Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));
  const topImpactServices = serviceImpactData.filter(s => s.impact > 0).slice(0, 5);
  return <div className="p-6 space-y-6 overflow-y-auto h-[calc(100vh-64px)]" data-id="element-1183">
      <Card className="!p-4" data-id="element-1184">
        <div className="flex items-center gap-4 flex-wrap" data-id="element-1185">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-1186">
            <Filter size={16} data-id="element-1187" />
            <span data-id="element-1188">Ansicht:</span>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-1189">
            {levels.map(level => {
            const isSelected = selectedLevel === level.id;
            const Icon = level.icon;
            return <button key={level.id} onClick={() => setSelectedLevel(level.id as any)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${isSelected ? 'bg-aldi-blue text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-1190">
                  <Icon size={14} data-id="element-1191" />
                  {level.name}
                </button>;
          })}
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1192">
        <KPICard title="Korrelation Services ↔ Rating" value={correlation.toFixed(2)} change={correlation > 0.6 ? 'Stark positiv' : correlation > 0.3 ? 'Moderat' : 'Schwach'} trend="up" icon={<TrendingUp size={18} data-id="element-1194" />} subtext="Pearson-Koeffizient" data-id="element-1193" />
        <KPICard title="Ø Services pro Markt" value={avgServices.toFixed(1)} change={`von ${services.length} möglich`} trend="up" icon={<Package size={18} data-id="element-1196" />} subtext="Durchschnitt" data-id="element-1195" />
        <KPICard title="Top Service-Effekt" value={`+${topImpactServices[0]?.impact.toFixed(2) || 0}★`} change={topImpactServices[0]?.name || '-'} trend="up" icon={<Star size={18} data-id="element-1198" />} subtext="Rating-Unterschied" data-id="element-1197" />
        <KPICard title="Ø Bewertung" value={avgRating.toFixed(2)} change="+0.15" trend="up" icon={<Star size={18} data-id="element-1200" />} subtext="Google Rating" data-id="element-1199" />
      </div>

      {selectedLevel === 'services' ? <>
          <Card data-id="element-1201">
            <div className="p-6" data-id="element-1202">
              <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-1203">
                Anzahl Services vs. Rating
              </h3>
              <p className="text-sm text-gray-500 mb-4" data-id="element-1204">
                Korrelation zwischen Service-Angebot und Bewertung
              </p>
              <div className="h-72" data-id="element-1205">
                <ResponsiveContainer width="100%" height="100%" data-id="element-1206">
                  <ScatterChart margin={{
                top: 20,
                right: 20,
                bottom: 20,
                left: 20
              }} data-id="element-1207">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-1208" />
                    <XAxis type="number" dataKey="serviceCount" name="Services" domain={[0, 8]} axisLine={false} tickLine={false} tick={{
                  fontSize: 11,
                  fill: '#9ca3af'
                }} label={{
                  value: 'Anzahl Services',
                  position: 'bottom',
                  offset: 0,
                  style: {
                    fontSize: 11,
                    fill: '#6b7280'
                  }
                }} data-id="element-1209" />
                    <YAxis type="number" dataKey="rating" name="Rating" domain={[3.2, 5]} axisLine={false} tickLine={false} tick={{
                  fontSize: 11,
                  fill: '#9ca3af'
                }} label={{
                  value: 'Rating',
                  angle: -90,
                  position: 'insideLeft',
                  style: {
                    fontSize: 11,
                    fill: '#6b7280'
                  }
                }} data-id="element-1210" />
                    <ZAxis type="number" dataKey="z" range={[100, 500]} data-id="element-1211" />
                    <Tooltip contentStyle={{
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  fontSize: '12px'
                }} content={({
                  active,
                  payload
                }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    return <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-100" data-id="element-1213">
                              <p className="font-medium text-gray-900 text-sm mb-2" data-id="element-1214">
                                {data.name.replace('ALDI Nord ', '')}
                              </p>
                              <div className="space-y-1 text-xs" data-id="element-1215">
                                <p className="text-gray-600" data-id="element-1216">
                                  <span className="font-medium" data-id="element-1217">Services:</span>{' '}
                                  {data.serviceCount}
                                </p>
                                <p className="text-gray-600" data-id="element-1218">
                                  <span className="font-medium" data-id="element-1219">Rating:</span>{' '}
                                  {data.rating.toFixed(1)} ★
                                </p>
                              </div>
                            </div>;
                  }
                  return null;
                }} data-id="element-1212" />
                    <Scatter name="Märkte" data={correlationData} fill="#00005f" data-id="element-1220">
                      {correlationData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.serviceCount >= avgServices ? '#639754' : '#E03C32'} fillOpacity={0.7} data-id="element-1221" />)}
                    </Scatter>
                  </ScatterChart>
                </ResponsiveContainer>
              </div>
            </div>
          </Card>

          <Card data-id="element-1222">
            <div className="p-6" data-id="element-1223">
              <h3 className="text-lg font-semibold text-gray-900 mb-6" data-id="element-1224">
                Service-Auswirkung auf Rating
              </h3>
              <div className="h-[400px]" data-id="element-1225">
                <ResponsiveContainer width="100%" height="100%" data-id="element-1226">
                  <BarChart data={serviceImpactData} layout="vertical" margin={{
                top: 5,
                right: 30,
                left: 140,
                bottom: 5
              }} data-id="element-1227">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={true} vertical={false} data-id="element-1228" />
                    <XAxis type="number" domain={[3, 5]} axisLine={false} tickLine={false} tick={{
                  fontSize: 11,
                  fill: '#9ca3af'
                }} data-id="element-1229" />
                    <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} tick={{
                  fontSize: 12,
                  fill: '#374151'
                }} width={130} data-id="element-1230" />
                    <Tooltip contentStyle={{
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  fontSize: '12px'
                }} formatter={(value: number, name: string) => [value.toFixed(2) + ' ★', name === 'avgRatingWith' ? 'Mit Service' : 'Ohne Service']} data-id="element-1231" />
                    <Bar dataKey="avgRatingWith" name="Mit Service" fill="#639754" radius={[0, 4, 4, 0]} barSize={16} data-id="element-1232" />
                    <Bar dataKey="avgRatingWithout" name="Ohne Service" fill="#E03C32" radius={[0, 4, 4, 0]} barSize={16} data-id="element-1233" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </Card>

          <Card data-id="element-1234">
            <div className="p-6" data-id="element-1235">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1236">
                Service-Übersicht
              </h3>
              <div className="overflow-x-auto" data-id="element-1237">
                <table className="w-full text-sm" data-id="element-1238">
                  <thead data-id="element-1239">
                    <tr className="border-b border-gray-200" data-id="element-1240">
                      <th className="text-left py-3 px-4 font-medium text-gray-500" data-id="element-1241">
                        Service
                      </th>
                      <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1242">
                        Filialen
                      </th>
                      <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1243">
                        Penetration
                      </th>
                      <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1244">
                        Ø Rating mit
                      </th>
                      <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1245">
                        Ø Rating ohne
                      </th>
                      <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1246">
                        Effekt
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-1247">
                    {serviceImpactData.map(service => {
                  const ServiceIcon = service.icon;
                  return <tr key={service.id} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-1248">
                          <td className="py-3 px-4" data-id="element-1249">
                            <div className="flex items-center gap-2" data-id="element-1250">
                              <ServiceIcon size={16} className="text-gray-400" data-id="element-1251" />
                              <span className="font-medium text-gray-900" data-id="element-1252">
                                {service.name}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-right text-gray-900" data-id="element-1253">
                            {service.marketsCount}
                          </td>
                          <td className="py-3 px-4 text-right" data-id="element-1254">
                            <div className="flex items-center justify-end gap-2" data-id="element-1255">
                              <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-1256">
                                <div className="h-full rounded-full" style={{
                            width: `${service.penetration}%`,
                            backgroundColor: '#00005f'
                          }} data-id="element-1257" />
                              </div>
                              <span className="text-gray-500 w-10 text-right" data-id="element-1258">
                                {service.penetration.toFixed(0)}%
                              </span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-right font-medium text-gray-900" data-id="element-1259">
                            {service.avgRatingWith.toFixed(2)} ★
                          </td>
                          <td className="py-3 px-4 text-right text-gray-500" data-id="element-1260">
                            {service.avgRatingWithout.toFixed(2)} ★
                          </td>
                          <td className="py-3 px-4 text-right" data-id="element-1261">
                            <span className={`font-medium ${service.impact > 0 ? 'text-green-600' : service.impact < 0 ? 'text-red-600' : 'text-gray-500'}`} data-id="element-1262">
                              {service.impact > 0 ? '+' : ''}
                              {service.impact.toFixed(2)} ★
                            </span>
                          </td>
                        </tr>;
                })}
                  </tbody>
                </table>
              </div>
            </div>
          </Card>
        </> : <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1263">
            <Card data-id="element-1264">
              <div className="p-6" data-id="element-1265">
                <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2" data-id="element-1266">
                  <ArrowUpRight className="text-green-600" size={20} data-id="element-1267" /> Märkte
                  mit meisten Services
                </h3>
                <div className="space-y-3 mt-4" data-id="element-1268">
                  {topServiceMarkets.map((market, index) => <motion.div key={market.id} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: index * 0.1
              }} className="flex items-center justify-between p-3 rounded-lg" style={{
                backgroundColor: '#e8f0e5'
              }} data-id="element-1269">
                      <div className="flex items-center gap-3" data-id="element-1270">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{
                    backgroundColor: '#639754'
                  }} data-id="element-1271">
                          {index + 1}
                        </div>
                        <div data-id="element-1272">
                          <div className="font-medium text-gray-900 text-sm" data-id="element-1273">
                            {market.name}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-1274">
                            {market.region}
                          </div>
                        </div>
                      </div>
                      <div className="text-right" data-id="element-1275">
                        <div className="font-bold text-lg" style={{
                    color: '#639754'
                  }} data-id="element-1276">
                          {market.services.length} Services
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-1277">
                          {market.rating.toFixed(1)} ★
                        </div>
                      </div>
                    </motion.div>)}
                </div>
              </div>
            </Card>

            <Card data-id="element-1278">
              <div className="p-6" data-id="element-1279">
                <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2" data-id="element-1280">
                  <ArrowDownRight className="text-red-600" size={20} data-id="element-1281" /> Märkte
                  mit wenigsten Services
                </h3>
                <div className="space-y-3 mt-4" data-id="element-1282">
                  {lowServiceMarkets.map((market, index) => <motion.div key={market.id} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: index * 0.1
              }} className="flex items-center justify-between p-3 rounded-lg" style={{
                backgroundColor: '#fce8e7'
              }} data-id="element-1283">
                      <div className="flex items-center gap-3" data-id="element-1284">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{
                    backgroundColor: '#E03C32'
                  }} data-id="element-1285">
                          !
                        </div>
                        <div data-id="element-1286">
                          <div className="font-medium text-gray-900 text-sm" data-id="element-1287">
                            {market.name}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-1288">
                            {market.region}
                          </div>
                        </div>
                      </div>
                      <div className="text-right" data-id="element-1289">
                        <div className="font-bold text-lg" style={{
                    color: '#E03C32'
                  }} data-id="element-1290">
                          {market.services.length} Services
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-1291">
                          {market.rating.toFixed(1)} ★
                        </div>
                      </div>
                    </motion.div>)}
                </div>
              </div>
            </Card>
          </div>

          <Card data-id="element-1292">
            <div className="p-6" data-id="element-1293">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1294">
                Service-Matrix nach Markt
              </h3>
              <div className="overflow-x-auto" data-id="element-1295">
                <table className="w-full text-sm" data-id="element-1296">
                  <thead data-id="element-1297">
                    <tr className="border-b border-gray-200" data-id="element-1298">
                      <th className="text-left py-3 px-4 font-medium text-gray-500 sticky left-0 bg-white" data-id="element-1299">
                        Markt
                      </th>
                      <th className="text-right py-3 px-2 font-medium text-gray-500" data-id="element-1300">
                        Rating
                      </th>
                      <th className="text-center py-3 px-2 font-medium text-gray-500" data-id="element-1301">
                        #
                      </th>
                      {services.map(service => {
                    const ServiceIcon = service.icon;
                    return <th key={service.id} className="text-center py-3 px-1 font-medium text-gray-500" title={service.name} data-id="element-1302">
                            <ServiceIcon size={14} className="mx-auto" data-id="element-1303" />
                          </th>;
                  })}
                    </tr>
                  </thead>
                  <tbody data-id="element-1304">
                    {marketsByServices.map(market => <tr key={market.id} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-1305">
                        <td className="py-2 px-4 font-medium text-gray-900 sticky left-0 bg-white text-xs" data-id="element-1306">
                          {market.name.replace('ALDI Nord ', '')}
                        </td>
                        <td className="py-2 px-2 text-right" data-id="element-1307">
                          <span className={`font-medium ${market.rating >= 4.3 ? 'text-green-600' : market.rating >= 3.8 ? 'text-gray-900' : 'text-red-600'}`} data-id="element-1308">
                            {market.rating.toFixed(1)}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-center font-medium text-gray-500" data-id="element-1309">
                          {market.services.length}
                        </td>
                        {services.map(service => <td key={service.id} className="py-2 px-1 text-center" data-id="element-1310">
                            {market.services.includes(service.id) ? <CheckCircle2 size={14} className="mx-auto text-green-500" data-id="element-1311" /> : <XCircle size={14} className="mx-auto text-gray-200" data-id="element-1312" />}
                          </td>)}
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          </Card>
        </>}
    </div>;
}